import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import {
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import isEqual from 'lodash.isequal';
import {
  ExternalAccount,
  ExternalAccountFormValues,
  ExternalAccountTopUpOption,
  ExternalAccountTopUpOptionFormValues,
  ExternalAccountWithIntegrationFormValues,
  PspIntegration,
  PspPartner,
} from '../../types';
import { Balance } from '../../../accounts/types';
import {
  useLazyGetAccountsPspPartnersQuery,
  useLazyGetExternalAccountsListQuery,
  useUpdateExternalAccountStatusMutation,
  useLazyGetExternalAccountQuery,
  useLazyGetPspIntegrationsQuery,
  useCreateExternalAccountWithIntegrationMutation,
  useLazyGetExternalAccountTopUpOptionsQuery,
  useLazyGetExternalAccountChangeLogsQuery,
  useUpdateExternalAccountCurrenciesMutation,
  useCreateExternalAccountMutation,
  useDeletePaymentAccountsMutation,
  useCreatePaymentsAccountsMutation,
  useUpdatePaymentsAccountsMutation,
} from '../../store/services';
import useGetAccountsAutocompleteList from '../../hooks/use-get-accounts';
import {
  clearExternalAccountState,
  setOpenPayNotificationVisible,
} from '../../store/slice';
import { PaymentType } from '../../../../store/dictionaries/types';
import { useLazyGetAccountBalancesQuery } from '../../../accounts/store/services';

import ROUTES from '../../../../routes';

const defaultFormValues = {
  pspPartnerId: '',
  accountId: '',
  iban: '',
  paymentType: '',
  currencyId: [],
  status: 'enabled',
};

const useFormFunctions = ({
  takeCount,
  search,
  setPanelIsOpen,
  pspPartners,
  accountId,
  paymentTypes,
  externalAccount,
  externalAccountTopUpOptions,
}: {
  takeCount?: number;
  search?: string;
  setPanelIsOpen: (data: boolean) => void;
  pspPartners?: PspPartner[];
  accountId?: string;
  paymentTypes?: PaymentType[];
  externalAccount?: ExternalAccount;
  externalAccountTopUpOptions?: ExternalAccountTopUpOption[];
}) => {
  const navigate = useNavigate();

  const { pspIntegrations, changeLogs } = useAppSelector(
    (state) => state.externalAccounts,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const [selectedPartner, setSelectedPartner] = useState<PspPartner>();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [globalError, setGlobalError] = useState('');
  const [activeTab, setActiveTab] = useState<
    'details' | 'top-up' | 'change-logs'
  >('details');

  const [availablePaymentTypes, setAvailablePaymentTypes] = useState<string[]>(
    [],
  );
  const [isOpenPayd, setIsOpenPayd] = useState(false);
  const [isPrimeVault, setIsPrimeVault] = useState(false);
  const [isKeel, setIsKeel] = useState(false);
  const [openPaydKeelIntegration, setOpenPaydKeelIntegration] = useState<
    PspIntegration | undefined
  >(undefined);
  const [integrationType, setIntegrationType] = useState<
    'with-integration' | 'without-integration'
  >('without-integration');
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [defaultTopUpOptions, setDefaultTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [topUpOptions, setTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [optionsErrors, setOptionsErrors] = useState<
    {
      id: string;
      currencyId?: string;
      paymentType?: string;
    }[]
  >([]);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);

  const [createExternalAccount, { isLoading, isSuccess }] =
    useCreateExternalAccountMutation();
  const [
    createExternalAccountWithIntegration,
    { isLoading: createInProgress, isSuccess: createIsSuccess },
  ] = useCreateExternalAccountWithIntegrationMutation();

  const [getAccountsPspPartners] = useLazyGetAccountsPspPartnersQuery();
  const [
    getBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetAccountBalancesQuery();
  const [getExternalAccountsList] = useLazyGetExternalAccountsListQuery();
  const [updateStatus, { isLoading: updateStatusInProgress }] =
    useUpdateExternalAccountStatusMutation();
  const [
    getExternalAccount,
    {
      isLoading: externalAccountIsLoading,
      isFetching: externalAccountIsFetching,
    },
  ] = useLazyGetExternalAccountQuery();
  const [getPspIntegrations] = useLazyGetPspIntegrationsQuery();
  const [
    getExternalAccountTopUpOptions,
    { isLoading: topUpIsLoading, isFetching: topUpIsFetching },
  ] = useLazyGetExternalAccountTopUpOptionsQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetExternalAccountChangeLogsQuery();
  const [
    updateExternalAccountCurrencies,
    {
      isLoading: updateExternalAccountCurrenciesInProgress,
      isSuccess: updateExternalAccountCurrenciesIsSuccess,
    },
  ] = useUpdateExternalAccountCurrenciesMutation();
  const [
    deletePaymentAccounts,
    {
      isLoading: deletePaymentAccountsInProgress,
      isSuccess: deletePaymentAccountsIsSuccess,
    },
  ] = useDeletePaymentAccountsMutation();
  const [
    createPaymentAccounts,
    {
      isLoading: createPaymentAccountsInProgress,
      isSuccess: createPaymentAccountsIsSuccess,
    },
  ] = useCreatePaymentsAccountsMutation();
  const [
    updatePaymentAccounts,
    {
      isLoading: updatePaymentAccountsInProgress,
      isSuccess: updatePaymentAccountsIsSuccess,
    },
  ] = useUpdatePaymentsAccountsMutation();

  const {
    autocompleteList: accounts,
    onSearch: onSearchAccounts,
    onClear,
    isLoading: accountsIsLoading,
  } = useGetAccountsAutocompleteList('&isClientAccount=true', accountId);

  const {
    reset,
    control,
    formState: { errors, isDirty },
    setError,
    handleSubmit,
    setValue,
    watch,
  } = useForm<ExternalAccountFormValues>({
    defaultValues: defaultFormValues,
  });

  const pspPartnerId = watch('pspPartnerId');
  const paymentType = watch('paymentType');
  const currencyId = watch('currencyId');
  const accountIdValue = watch('accountId');
  const status = watch('status');
  const caspId = watch('caspId');

  const prevStatus = usePrevious(status);
  const prevPaymentType = usePrevious(paymentType);
  const prevCaspId = usePrevious(caspId);
  const prevAccountId = usePrevious(accountIdValue);
  const prevPspPartnerId = usePrevious(pspPartnerId);

  const dispatch = useAppDispatch();

  const isDirtyValue = accountId
    ? (status && prevStatus && status !== prevStatus) ||
      !isEqual(topUpOptions, defaultTopUpOptions)
    : isDirty;

  const deletePaymentAccountsAction = async () => {
    await deletePaymentAccounts(selectedOptionId);

    if (optionsErrors?.find((item) => item.id === selectedOptionId)) {
      setOptionsErrors(
        optionsErrors?.filter((item) => item.id !== selectedOptionId),
      );
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    let response;

    if (!accountId) {
      const body: ExternalAccountFormValues = {
        pspPartnerId: values.pspPartnerId,
        accountId: values.accountId,
        iban: values.iban,
        paymentType: values.paymentType,
        currencyId: values.currencyId,
        currenciesIds: values.currencyId,
        active: Boolean(values.status === 'enabled'),
      };

      if (values?.caspId) {
        body.caspId = values.caspId;
      }

      response = (await createExternalAccount(body)) as {
        error: ResponseError | { data: { message: string } };
        data?: { data?: ExternalAccount };
      };
    } else {
      setOptionsErrors([]);

      const optionsForCreate: ExternalAccountTopUpOptionFormValues[] = [];
      const optionForUpdate: ExternalAccountTopUpOptionFormValues[] = [];
      const optionForWithErrors: ExternalAccountTopUpOptionFormValues[] = [];

      topUpOptions?.forEach((item) => {
        if (!item?.paymentType || !item?.currencyId) {
          optionForWithErrors.push(item);
        }

        if (item?.id?.includes('option')) {
          optionsForCreate.push(item);
        }

        if (!item?.id?.includes('option')) {
          const currentDefaultOption = defaultTopUpOptions?.find(
            (defaultOption) => defaultOption?.id === item?.id,
          );

          if (currentDefaultOption && !isEqual(currentDefaultOption, item)) {
            optionForUpdate.push(item);
          }
        }
      });

      if (!optionForWithErrors?.length) {
        if (optionsForCreate?.length) {
          await Promise.all(
            optionsForCreate?.map(async (item) => {
              const body: ExternalAccountTopUpOptionFormValues = {
                paymentType: item?.paymentType,
                currencyId: item?.currencyId,
                bankAccountHolderName: item?.bankAccountHolderName,
                accountNumber: item?.accountNumber,
                bankAddress: item?.bankAddress,
                bic: item?.bic,
                iban: item?.iban,
                bankName: item?.bankName,
                pspIntegrationAccountId: externalAccount?.id,
              };

              if (item?.thirdPartyReference) {
                body.thirdPartyReference = item.thirdPartyReference;
              }

              if (
                item?.routingCodeEntries?.filter(
                  (code) => code?.routingCodeKey && code?.routingCodeValue,
                )?.length
              ) {
                body.routingCodeEntries = item?.routingCodeEntries
                  ?.filter(
                    (code) => code?.routingCodeKey && code?.routingCodeValue,
                  )
                  ?.map((code) => ({
                    routingCodeKey: code.routingCodeKey,
                    routingCodeValue: code.routingCodeValue,
                  }));
              }

              await createPaymentAccounts(body);
            }),
          );
        }

        if (optionForUpdate?.length) {
          await Promise.all(
            optionForUpdate?.map(async (item) => {
              const body: ExternalAccountTopUpOptionFormValues = {
                paymentType: item?.paymentType,
                currencyId: item?.currencyId,
                bankAccountHolderName: item?.bankAccountHolderName,
                accountNumber: item?.accountNumber,
                bankAddress: item?.bankAddress,
                bic: item?.bic,
                iban: item?.iban,
                bankName: item?.bankName,
              };

              if (item?.thirdPartyReference) {
                body.thirdPartyReference = item.thirdPartyReference;
              }

              if (
                item?.routingCodeEntries?.filter(
                  (code) => code.routingCodeKey && code.routingCodeValue,
                )?.length
              ) {
                body.routingCodeEntries = item?.routingCodeEntries
                  ?.filter(
                    (code) => code.routingCodeKey && code.routingCodeValue,
                  )
                  ?.map((code) => ({
                    routingCodeKey: code.routingCodeKey,
                    routingCodeValue: code.routingCodeValue,
                  }));
              }

              const currentDefaultTopUpOptions = defaultTopUpOptions?.find(
                (option) => option?.id === item?.id,
              );

              if (
                !item?.routingCodeEntries?.filter(
                  (code) => code.routingCodeKey && code.routingCodeValue,
                ) &&
                currentDefaultTopUpOptions &&
                currentDefaultTopUpOptions?.routingCodeEntries?.length
              ) {
                body.routingCodeEntries = null;
              }

              await updatePaymentAccounts({
                id: item?.id as string,
                body,
              });
            }),
          );
        }

        response = (await updateStatus({
          ids: [accountId],
          active: Boolean(values.status === 'enabled'),
        })) as { error?: ResponseError };

        await updateExternalAccountCurrencies({
          id: accountId,
          currenciesIds: values.currencyId,
        });
      } else {
        const optionErrors: {
          id: string;
          currencyId?: string;
          paymentType?: string;
        }[] = [];

        optionForWithErrors?.forEach((item) => {
          const body: {
            id: string;
            currencyId?: string;
            paymentType?: string;
          } = {
            id: item.id as string,
          };

          if (!item?.currencyId) {
            body.currencyId = 'Currency is required';
          }

          if (!item.paymentType) {
            body.paymentType = 'Payment Type is required';
          }

          optionErrors.push(body);
        });

        setOptionsErrors(optionErrors);
      }
    }

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError,
        });
      }
    }
  });

  const onSubmitWithIntegration = handleSubmit(async (values) => {
    setGlobalError('');

    const body: ExternalAccountWithIntegrationFormValues = {
      accountsIds: [values.accountId],
      integrationId: selectedPartner?.pspIntegrationId as string,
      currenciesIds: values.currencyId,
    };

    if (values.caspId) {
      body.caspId = values.caspId;
    }

    const response = (await createExternalAccountWithIntegration(body)) as {
      error: ResponseError | { data: { message: string } };
      data?: { data?: ExternalAccount };
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError,
      });
    }
  });

  const handleClose = () => {
    reset(defaultFormValues);
    dispatch(clearExternalAccountState());

    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${ROUTES.EXTERNAL_ACCOUNTS_LIST}${search || ''}`);
    }, 100);
  };

  const getAccountsBalancesAction = useCallback(async (id: string) => {
    const response = await getBalances(id);

    if (response) {
      setBalances(
        response?.data?.data?.edges?.map((item) => item.node) as Balance[],
      );
    }
  }, []);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=PSP_INTEGRATION_ACCOUNT&entityId=${accountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [accountId],
  );

  useEffect(() => {
    if (deletePaymentAccountsIsSuccess) {
      setDeleteConfirmationVisible(false);
      setSelectedOptionId('');
    }
  }, [deletePaymentAccountsIsSuccess]);

  useEffect(() => {
    if (externalAccountTopUpOptions) {
      const nextTopUpOptions = externalAccountTopUpOptions?.map((item) => ({
        id: item?.id,
        paymentType: item?.paymentType,
        currencyId: item?.currencyId,
        bankAccountHolderName: item?.bankAccountHolderName,
        accountNumber: item?.accountNumber,
        bankAddress: item?.bankAddress,
        bic: item?.bic,
        iban: item?.iban,
        bankName: item?.bankName,
        thirdPartyReference: item?.thirdPartyReference,
        pspIntegrationAccountId: item?.pspIntegrationAccountId,
        routingCodeEntries:
          item?.routingCodeEntries?.map((code) => ({
            id: uuid(),
            routingCodeKey: code?.routingCodeKey,
            routingCodeValue: code?.routingCodeValue,
          })) || [],
      }));

      setDefaultTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
      setTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
    }
  }, [externalAccountTopUpOptions]);

  useEffect(() => {
    if (
      !accountId &&
      ((((!isPrimeVault && !isOpenPayd && !isKeel) ||
        (isPrimeVault && integrationType === 'without-integration') ||
        (isOpenPayd && integrationType === 'without-integration') ||
        (isKeel && integrationType === 'without-integration')) &&
        paymentType !== prevPaymentType) ||
        (isPrimeVault &&
          integrationType === 'with-integration' &&
          caspId !== prevCaspId) ||
        accountIdValue !== prevAccountId)
    ) {
      setValue('currencyId', []);
    }
  }, [
    isPrimeVault,
    isOpenPayd,
    integrationType,
    prevCaspId,
    prevPaymentType,
    caspId,
    paymentType,
    accountIdValue,
    prevAccountId,
    accountId,
    isKeel,
  ]);

  useEffect(() => {
    if (
      activeTab === 'top-up' &&
      accountId &&
      externalAccount?.pspIntegrationPayment?.paymentsTypes?.length &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
      )
    ) {
      getExternalAccountTopUpOptions({
        id: accountId,
      });
    }
  }, [activeTab, externalAccount?.pspIntegrationPayment?.paymentsTypes]);

  useEffect(() => {
    if (
      activeTab === 'change-logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (pspPartnerId && pspPartners && pspIntegrations) {
      const currentPspPartner = pspPartners?.find(
        (item) => item.id === pspPartnerId,
      );

      if (currentPspPartner) {
        const currentPspIntegration = pspIntegrations?.find(
          (item) => item.id === currentPspPartner?.pspIntegrationId,
        );

        if (currentPspIntegration) {
          if (currentPspIntegration.name === 'OPEN_PAYD') {
            setIsOpenPayd(true);
            setOpenPaydKeelIntegration(currentPspIntegration);
            setIsPrimeVault(false);
            setIsKeel(false);
          } else if (currentPspIntegration.name === 'Keel') {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(currentPspIntegration);
            setIsPrimeVault(false);
            setIsKeel(true);
          } else if (currentPspIntegration.name === 'PRIME_VAULT_WALLET') {
            setIsPrimeVault(true);
            setOpenPaydKeelIntegration(undefined);
            setIsOpenPayd(false);
            setIsKeel(false);
          } else {
            setIsOpenPayd(false);
            setOpenPaydKeelIntegration(undefined);
            setIsPrimeVault(false);
            setIsKeel(false);
          }
        }
      }
    }
  }, [pspPartnerId, pspIntegrations, pspPartners]);

  useEffect(() => {
    if (externalAccount && pspIntegrations) {
      const currentPspIntegration = pspIntegrations?.find(
        (item) => item.id === externalAccount?.pspPartner?.pspIntegrationId,
      );

      if (currentPspIntegration) {
        if (currentPspIntegration.name === 'OPEN_PAYD') {
          setIsOpenPayd(true);
          setOpenPaydKeelIntegration(currentPspIntegration);
          setIsPrimeVault(false);
          setIsKeel(false);
        } else if (currentPspIntegration.name === 'Keel') {
          setIsOpenPayd(false);
          setOpenPaydKeelIntegration(currentPspIntegration);
          setIsPrimeVault(false);
          setIsKeel(true);
        } else if (currentPspIntegration.name === 'PRIME_VAULT_WALLET') {
          setIsPrimeVault(true);
          setOpenPaydKeelIntegration(undefined);
          setIsOpenPayd(false);
          setIsKeel(false);
        } else {
          setIsOpenPayd(false);
          setIsPrimeVault(false);
          setOpenPaydKeelIntegration(undefined);
          setIsOpenPayd(false);
          setIsKeel(false);
        }
      }
    }
  }, [externalAccount, pspIntegrations]);

  useEffect(() => {
    if (accountIdValue) {
      getAccountsBalancesAction(accountIdValue);
    }
  }, [accountIdValue]);

  useEffect(() => {
    if (!pspIntegrations) {
      getPspIntegrations();
    }
  }, [pspIntegrations]);

  useEffect(() => {
    if (accountId) {
      getExternalAccount(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (externalAccount) {
      getAccountsBalancesAction(externalAccount?.account?.id);
    }
  }, [externalAccount]);

  useEffect(() => {
    if (externalAccount && paymentTypes && balances) {
      const types: string[] = [];

      externalAccount?.pspIntegrationPayment?.paymentsTypes?.forEach((item) => {
        if (paymentTypes?.find((typeValue) => typeValue.paymentType === item)) {
          types.push(
            paymentTypes?.find((typeValue) => typeValue.paymentType === item)
              ?.name as string,
          );
        }
      });

      setAvailablePaymentTypes(types);
    }
  }, [externalAccount, paymentTypes, balances]);

  useEffect(() => {
    if (
      (isSuccess ||
        createIsSuccess ||
        updateExternalAccountCurrenciesIsSuccess ||
        createPaymentAccountsIsSuccess ||
        updatePaymentAccountsIsSuccess) &&
      !createPaymentAccountsInProgress &&
      !updatePaymentAccountsInProgress
    ) {
      handleClose();

      if (isSuccess) {
        getExternalAccountsList(
          `?take=${takeCount}${search ? search.replace('?', '&') : ''}`,
        );
      }

      if (createIsSuccess) {
        dispatch(setOpenPayNotificationVisible(true));

        setTimeout(() => {
          dispatch(setOpenPayNotificationVisible(false));
        }, 10000);
      }
    }
  }, [
    isSuccess,
    createIsSuccess,
    updateExternalAccountCurrenciesIsSuccess,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
  ]);

  useEffect(() => {
    if (externalAccount) {
      reset({
        status: externalAccount.active ? 'enabled' : 'disabled',
        caspId: externalAccount?.caspId || '',
        currencyId: externalAccount.currenciesIds,
      });
    }
  }, [externalAccount]);

  useEffect(() => {
    if (!accountId && pspPartnerId && pspPartnerId !== prevPspPartnerId) {
      setIntegrationType('without-integration');
    }
  }, [pspPartnerId, prevPspPartnerId]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (!pspPartners) {
      getAccountsPspPartners();
    }
  }, []);

  return {
    selectedPartner,
    setSelectedPartner,
    balances,
    globalError,
    isLoading: isLoading || createInProgress,
    accounts,
    onSearchAccounts,
    onClear,
    accountsIsLoading,
    control,
    errors,
    isDirty: isDirtyValue,
    setValue,
    pspPartnerId,
    paymentType,
    currencyId,
    onSubmit,
    handleClose,
    availablePaymentTypes,
    balancesIsLoading,
    balancesIsFetching,
    updateStatusInProgress,
    accountIdValue,
    isOpenPayd,
    isKeel,
    externalAccountIsLoading:
      externalAccountIsLoading || externalAccountIsFetching,
    integrationType,
    setIntegrationType,
    openPaydKeelIntegration,
    onSubmitWithIntegration,
    createInProgress,
    activeTab,
    setActiveTab,
    topUpIsLoading: topUpIsLoading || topUpIsFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    updateExternalAccountCurrenciesInProgress,
    isPrimeVault,
    caspId,
    setIsOpenPayd,
    setIsPrimeVault,
    setIsKeel,
    setSelectedOptionId,
    deleteConfirmationVisible,
    setDeleteConfirmationVisible,
    deletePaymentAccountsAction,
    deletePaymentAccountsInProgress,
    topUpOptions,
    setTopUpOptions,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
    optionsErrors,
    setOptionsErrors,
  };
};

export default useFormFunctions;
